import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { getBlogPost } from "@/content/blog";
import { redirectFor } from "@/content/redirects";
import {
  CONTENT_CACHE_CONTROL,
  CONTENT_NOT_FOUND_CACHE_CONTROL,
} from "@/content/cachePolicy";
import { blogPostingSchema, jsonLdVerbatim } from "@/content/schema";
import BlogPostPage from "./-components/BlogPostPage";
import { NotFoundPage } from "@/components/landing/LandingNotFound";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    // Before the fetch: the published site 301s a handful of slugs that the CMS
    // still reports as live, and serving them would duplicate the target page.
    const moved = redirectFor(`/blog/${params.slug}`);
    if (moved) {
      throw redirect({ to: moved, statusCode: 301 });
    }
    const post = await getBlogPost({ data: params.slug });
    // A real 404 rather than a 200 rendering "not found": an unpublished or
    // renamed post has to drop out of the index, not linger as a soft 404.
    if (!post) {
      throw notFound();
    }
    return post;
  },
  /**
   * `loaderData` is absent exactly when the loader threw `notFound()`, which is
   * the one response that must not be held by a shared cache.
   */
  headers: ({ loaderData }) => ({
    "Cache-Control": loaderData
      ? CONTENT_CACHE_CONTROL
      : CONTENT_NOT_FOUND_CACHE_CONTROL,
  }),
  // `head` runs before the loader settles on a client-side navigation, so the
  // absence of loaderData is expected rather than a fault.
  head: ({ loaderData }) =>
    loaderData
      ? {
          scripts: [
            {
              type: "application/ld+json",
              children: jsonLdVerbatim(blogPostingSchema(loaderData)),
            },
          ],
        }
      : {},
  component: BlogPostRoute,
  notFoundComponent: NotFoundPage,
});

function BlogPostRoute() {
  return <BlogPostPage post={Route.useLoaderData()} />;
}
